import { getIn, useFormikContext } from 'formik';
import { useEffect, useRef } from 'react';

import { hasValue } from '../utils/equality';
import {
  type FirstProductRowStatus,
  isFirstProductRowPending,
  withoutFirstProductRowPending,
} from '../utils/locationRows';

type Params = {
  productsPath: string;
  plantId: string;
  /** The location's products not yet in a row; undefined until loaded. */
  productsLeft: number | undefined;
  onAdd: () => void;
};

/** Adds the first product row to a location the user just picked, once its products load. */
export const useFirstProductRow = ({
  productsPath,
  plantId,
  productsLeft,
  onAdd,
}: Params): void => {
  const { status, setStatus, values } = useFormikContext<unknown>();
  const rowCount = (getIn(values, productsPath) as unknown[] | undefined)?.length ?? 0;

  const isFirstRowPending = isFirstProductRowPending(
    status as FirstProductRowStatus,
    productsPath,
    plantId,
  );
  // Two locations clearing in one render overwrite each other's status, so clear again until it
  // sticks; the ref keeps those reruns and StrictMode's double run from adding a second row.
  const addedForPlantIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isFirstRowPending) {
      addedForPlantIdRef.current = null;
      return;
    }
    if (!hasValue(productsLeft)) return;
    setStatus(
      withoutFirstProductRowPending(status as FirstProductRowStatus, productsPath, plantId),
    );
    if (addedForPlantIdRef.current === plantId) return;
    addedForPlantIdRef.current = plantId;
    if (rowCount === 0 && productsLeft > 0) {
      onAdd();
    }
  }, [status, isFirstRowPending, plantId, productsLeft, rowCount]);
};
